import React, { useMemo, useState, useEffect, useCallback } from 'react';
import '../super-admin.css';
import { Typography, Card, Table, Tag, Space, Button, Segmented, message } from 'antd';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const ActiveInactiveControl = () => {
  const { Title, Text } = Typography;
  const [snippets, setSnippets] = useState([]);
  const [filter, setFilter] = useState('All');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const counts = useMemo(() => ({
    active: snippets.filter(s => s.status === 'active').length,
    inactive: snippets.filter(s => s.status !== 'active').length,
  }), [snippets]);

  const data = useMemo(() => {
    if (filter === 'Active') return snippets.filter(s => s.status === 'active');
    if (filter === 'Inactive') return snippets.filter(s => s.status !== 'active');
    return snippets;
  }, [snippets, filter]);

  const loadSnippets = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch websites with their snippets then flatten
      const params = new URLSearchParams({ limit: '100', offset: '0' });
      const res = await fetch(`${apiBase}/admin/website-snippets?${params}`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to load snippets');
      const body = await res.json();
      const websites = body.websites || [];
      const flat = websites.flatMap(w =>
        (w.snippets || []).map(sn => ({
          id: sn.id,
          name: sn.snippet_name,
          website: w.business_website || w.user_name || '—',
          status: sn.status,
        }))
      );
      setSnippets(flat);
    } catch (e) {
      message.error(e.message || 'Error loading snippets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSnippets();
  }, [loadSnippets]);

  const toggleOne = async (id, to) => {
    try {
      // optimistic update
      setSnippets(prev => prev.map(s => (s.id === id ? { ...s, status: to } : s)));
      const res = await fetch(`${apiBase}/admin/website-snippets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: to })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update snippet');
      message.success(`Snippet #${id} set to ${to}`);
    } catch (e) {
      message.error(e.message || 'Update failed');
      // revert by reloading
      loadSnippets();
    }
  };

  const bulk = async (to) => {
    if (selectedRowKeys.length === 0) { message.info('Select at least one row'); return; }
    setLoading(true);
    try {
      // Optimistic UI
      setSnippets(prev => prev.map(s => (selectedRowKeys.includes(s.id) ? { ...s, status: to } : s)));
      await Promise.all(selectedRowKeys.map(async (id) => {
        const res = await fetch(`${apiBase}/admin/website-snippets/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ status: to })
        });
        if (!res.ok) throw new Error('Failed');
      }));
      message.success(`${to === 'active' ? 'Activated' : 'Deactivated'} ${selectedRowKeys.length} snippet(s)`);
      setSelectedRowKeys([]);
    } catch (e) {
      message.error('Bulk update failed');
      await loadSnippets();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 12 }}>Active / Inactive Control</Title>
      <Card style={{ borderRadius: 14 }} loading={loading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space wrap>
            <Segmented
              value={filter}
              onChange={setFilter}
              options={['All', 'Active', 'Inactive']}
            />
            <Text>
              <Tag color="green">Active: {counts.active}</Tag>
              <Tag>Inactive: {counts.inactive}</Tag>
              <Tag color="blue">Total: {snippets.length}</Tag>
            </Text>
            <Button type="primary" onClick={() => bulk('active')} disabled={loading}>Activate Selected</Button>
            <Button danger onClick={() => bulk('inactive')} disabled={loading}>Deactivate Selected</Button>
          </Space>

          <Table
            rowKey="id"
            dataSource={data}
            pagination={false}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (r) => ({
                disabled: false,
              }),
            }}
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
              { title: 'Name', dataIndex: 'name', key: 'name' },
              { title: 'Website', dataIndex: 'website', key: 'website', width: 200, render: (w) => w },
              { title: 'Status', dataIndex: 'status', key: 'status', width: 140,
                render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s}</Tag> },
              { title: 'Actions', key: 'actions', width: 220, render: (_, r) => (
                <Space>
                  <Button size="small" onClick={() => toggleOne(r.id, 'active')} disabled={r.status === 'active' || loading}>Activate</Button>
                  <Button size="small" danger onClick={() => toggleOne(r.id, 'inactive')} disabled={r.status !== 'active' || loading}>Deactivate</Button>
                </Space>
              ) },
            ]}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ActiveInactiveControl;
